import { Button } from "~/atoms/button";
import DocumentIcon from "./DocumentIcon";
import { useState, useEffect } from "react";
import { type OfficeFileType, type OfficeVersion } from "~/api/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";
import { useTranslation } from "react-i18next";
import { analyzeTrackChanges } from "~/helpers/docx-track-changes";
import { downloadDocument, getOfficeURIScheme } from "~/api/v2/requests";
import { cn } from "~/lib/utils";
import { Spinner } from "~/atoms/spinner";
import { DocumentVersionStatus } from "~/config/types";
import { useSpeEditPermissions } from "~/hooks/useEditPermissions";

interface Props {
  version: OfficeVersion;
}

export const officeFileTypeToTitle: Record<OfficeFileType, string> = {
  docx: "Word",
  xlsx: "Excel",
  pptx: "PowerPoint",
};

export const OfficeDesktopButton = ({ version }: Props) => {
  const { t } = useTranslation();
  const { canNotEditSpeDraftFile, isSpeUserWithoutSso } =
    useSpeEditPermissions(version);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showTrackChangesWarning, setShowTrackChangesWarning] = useState(false);
  const [isCheckingTrackChanges, setIsCheckingTrackChanges] = useState(false);
  const [isOpeningDocument, setIsOpeningDocument] = useState(false);

  // Check track changes proactively in the background
  useEffect(() => {
    const checkTrackChanges = async () => {
      const shouldCheck =
        version.status === DocumentVersionStatus.draft &&
        version.fileType === "docx";

      if (!shouldCheck) return;

      setIsCheckingTrackChanges(true);
      try {
        const blob = await downloadDocument(version.id);
        const status = await analyzeTrackChanges(blob);
        setShowTrackChangesWarning(!status.hasTrackRevisionsSetting);
      } catch {
        setShowTrackChangesWarning(false);
      } finally {
        setIsCheckingTrackChanges(false);
      }
    };

    void checkTrackChanges();
  }, [version.status, version.fileType, version.id]);

  const openInOffice = async () => {
    setIsOpeningDocument(true);
    try {
      const { uri } = await getOfficeURIScheme(version.id);
      window.location.href = uri;
    } finally {
      setIsOpeningDocument(false);
    }
  };

  const handleOpenInOffice = async () => {
    if (isCheckingTrackChanges) {
      return;
    }

    if (!showTrackChangesWarning) {
      await openInOffice();
    } else {
      setIsDialogOpen(true);
    }
  };

  const handleConfirm = async () => {
    setIsDialogOpen(false);
    await openInOffice();
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
  };

  const isLoading = isCheckingTrackChanges || isOpeningDocument;

  if (isSpeUserWithoutSso || canNotEditSpeDraftFile) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block w-fit">
              <Button variant="outline" disabled>
                <DocumentIcon type={version.fileType} variant="product" />
                {t("commons.workflow.openInOffice", {
                  officeType: officeFileTypeToTitle[version.fileType],
                })}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isSpeUserWithoutSso
                ? t("commons.auth.ssoRequiredToEdit")
                : t("commons.auth.cantEditSpeDraftFile")}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (isLoading) {
    return (
      <Button variant="outline" disabled>
        <Spinner className="size-4 text-blue-600" />
        {t("commons.workflow.openInOffice", {
          officeType: officeFileTypeToTitle[version.fileType],
        })}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={handleOpenInOffice}
        disabled={isLoading}
      >
        {isLoading ? (
          <Spinner className="size-4 text-blue-600" />
        ) : (
          <DocumentIcon type={version.fileType} variant="product" />
        )}
        {t("commons.workflow.openInOffice", {
          officeType: officeFileTypeToTitle[version.fileType],
        })}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className={cn("max-w-md", showTrackChangesWarning && "bg-yellow-50")}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-900">
              {t("modal.officeDesktop.trackChangesDisabledTitle")}
            </DialogTitle>
            <DialogDescription className="text-amber-900">
              {t("modal.officeDesktop.trackChangesDisabledDescription")}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isOpeningDocument}
            >
              {t("modal.officeDesktop.cancel")}
            </Button>
            <Button
              variant="outline"
              onClick={handleConfirm}
              disabled={isOpeningDocument}
            >
              {isOpeningDocument ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <DocumentIcon type={version.fileType} variant="product" />
              )}
              {t("commons.workflow.openInOffice", {
                officeType: officeFileTypeToTitle[version.fileType],
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
